import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getProgram, getPrintable, type Program, type Printable } from '@/services/content'
import Icon from '@/components/ui/Icon'
import Button from '@/components/ui/Button'
import { useEnsureAccess } from '@/components/Gate'
import { programGateState } from '@/store/access'
import styles from './ProgramDetail.module.css'

export default function ProgramDetail() {
  const { programId } = useParams<{ programId: string }>()
  const navigate = useNavigate()
  const [program, setProgram] = useState<Program | null>(null)
  const [linkedPrintables, setLinkedPrintables] = useState<Printable[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedDay, setExpandedDay] = useState<number | null>(null)
  const { access } = useEnsureAccess()

  useEffect(() => {
    if (!programId) return

    getProgram(programId)
      .then(async (p) => {
        if (p) {
          setProgram(p)
          const printables = await Promise.all(
            p.linkedPrintableIds.map(id => getPrintable(id))
          )
          setLinkedPrintables(printables.filter(Boolean) as Printable[])
        }
      })
      .finally(() => setLoading(false))
  }, [programId])

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    )
  }

  if (!program) {
    return (
      <div className={styles.container}>
        <p>Program not found</p>
        <Button onClick={() => navigate('/programs')}>Back to Programs</Button>
      </div>
    )
  }

  // Programs are premium-only — locked during the trial too.
  const pgate = programGateState(access)
  if (pgate !== 'premium' && pgate !== 'loading') {
    return (
      <div className={styles.container}>
        <button className={styles.backButton} onClick={() => navigate('/programs')}>
          ← Back
        </button>
        <div style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}><Icon name="lock" size={22} /></div>
          <h2 style={{ color: '#2D3436', fontSize: 20, fontWeight: 800, margin: '0 0 6px' }}>
            {program.title}
          </h2>
          <p style={{ color: '#2D3436', opacity: 0.7, margin: '0 0 24px' }}>
            Guided programs are a premium feature. Subscribe to unlock all{' '}
            multi-day programs.
          </p>
          <Button variant="primary" fullWidth onClick={() => navigate('/subscription')}>
            See plans
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <button className={styles.backButton} onClick={() => navigate('/programs')}>
        ← Back
      </button>

      {/* Cover image hero */}
      {program.coverImage ? (
        <div className={styles.coverImage}>
          <img src={program.coverImage} alt={program.title} className={styles.coverImg} />
          <div className={styles.coverOverlay}>
            <h1 className={styles.coverTitle}>{program.title}</h1>
            <p className={styles.coverSubtitle}>{program.subtitle}</p>
            <div className={styles.coverMeta}>
              <span>{program.duration} days</span>
              <span>·</span>
              <span>{program.isPremium ? 'Premium' : 'Free'}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.cover} style={{ backgroundColor: program.coverColor }}>
          <span className={styles.coverIcon}><Icon name={program.icon} size={48} /></span>
          <h1 className={styles.coverTitle}>{program.title}</h1>
          <p className={styles.coverSubtitle}>{program.subtitle}</p>
          <div className={styles.coverMeta}>
            <span>{program.duration} days</span>
            <span>·</span>
            <span>{program.isPremium ? 'Premium' : 'Free'}</span>
          </div>
        </div>
      )}

      <div className={styles.content}>
        {/* Highlights */}
        {program.highlights && program.highlights.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>What you'll learn</h2>
            <ul className={styles.highlightsList}>
              {program.highlights.map((item, i) => (
                <li key={i} className={styles.highlightItem}>
                  <span className={styles.highlightCheck}>✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* What to expect */}
        {program.whatToExpect?.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>What to expect</h2>
            <ul className={styles.bulletList}>
              {program.whatToExpect.map((item, i) => (
                <li key={i} className={styles.bulletItem}>{item}</li>
              ))}
            </ul>
            {program.sectionImages?.[0] && (
              <img
                src={program.sectionImages[0]}
                alt=""
                className={styles.sectionImage}
              />
            )}
          </section>
        )}

        {/* Who is this for */}
        {program.whoIsThisFor?.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Is this for you?</h2>
            <ul className={styles.bulletList}>
              {program.whoIsThisFor.map((item, i) => (
                <li key={i} className={styles.bulletItem}>{item}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Our approach / methodology */}
        {program.methodology?.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Our approach</h2>
            <ul className={styles.bulletList}>
              {program.methodology.map((item, i) => (
                <li key={i} className={styles.bulletItem}>{item}</li>
              ))}
            </ul>
            {program.sectionImages?.[1] && (
              <img
                src={program.sectionImages[1]}
                alt=""
                className={styles.sectionImage}
              />
            )}
          </section>
        )}

        {/* Expected outcomes */}
        {program.expectedOutcomes && program.expectedOutcomes.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Expected outcomes</h2>
            <ul className={styles.outcomesList}>
              {program.expectedOutcomes.map((outcome, i) => (
                <li key={i} className={styles.outcomeItem}>
                  <span className={styles.outcomeCheck}><Icon name="check" size={22} /></span>
                  <span>{outcome}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Included printables */}
        {linkedPrintables.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Included printables</h2>
            <div className={styles.printableList}>
              {linkedPrintables.map((p) => (
                <button
                  key={p.id}
                  className={styles.printableCard}
                  onClick={() => navigate(`/printable/${p.id}`)}
                >
                  <span className={styles.printableIcon}></span>
                  <div>
                    <p className={styles.printableName}>{p.title}</p>
                    <p className={styles.printablePages}>{p.pages} pages</p>
                  </div>
                  <span className={styles.printableArrow}>→</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Daily breakdown */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            Daily breakdown ({program.duration} days)
          </h2>
          <div className={styles.dayList}>
            {program.days.map((day) => (
              <button
                key={day.day}
                className={`${styles.dayCard} ${expandedDay === day.day ? styles.dayExpanded : ''}`}
                onClick={() => setExpandedDay(expandedDay === day.day ? null : day.day)}
              >
                <div className={styles.dayHeader}>
                  <span className={styles.dayNumber}>Day {day.day}</span>
                  <span className={styles.dayTitle}>{day.title}</span>
                  <span className={styles.dayArrow}>
                    {expandedDay === day.day ? '−' : '+'}
                  </span>
                </div>
                {expandedDay === day.day && (
                  <div className={styles.dayContent}>
                    <p className={styles.dayDescription}>{day.description}</p>
                    {day.tip && (
                      <div className={styles.dayTip}>
                        <strong>Tip:</strong> {day.tip}
                      </div>
                    )}
                    {day.milestone && (
                      <div className={styles.dayMilestone}>
                         {day.milestone}
                      </div>
                    )}
                  </div>
                )}
              </button>
            ))}
          </div>
        </section>

        <div className={styles.footer}>
          <Button fullWidth size="lg">
            {program.isPremium ? 'Start Rs 1 Trial to Begin' : 'Start Program'}
          </Button>
        </div>
      </div>
    </div>
  )
}
